"use client";

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { IconButton } from "./IconButton";

export interface PaginationProps {
  /** 현재 페이지 번호 (1부터 시작) */
  currentPage: number;
  /** 전체 페이지 수 */
  totalPages: number;
  /** 페이지 변경 콜백 */
  onPageChange: (page: number) => void;
  /** 현재 페이지 크기 */
  pageSize?: number;
  /** 페이지 크기 변경 콜백 */
  onPageSizeChange?: (size: number) => void;
  /** 전체 항목 수 */
  totalCount?: number;
  /** 페이지 크기 옵션 */
  pageSizeOptions?: number[];
  /** 추가 클래스 */
  className?: string;
}

/**
 * 페이지네이션 컴포넌트
 * 이전/다음 버튼, 페이지 번호, 페이지 크기 선택 제공
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  pageSize = 50,
  onPageSizeChange,
  totalCount,
  pageSizeOptions = [25, 50, 100],
  className = "",
}: PaginationProps) {
  // 페이지가 1개 이하면 표시하지 않음
  if (totalPages <= 1 && !onPageSizeChange) {
    return null;
  }

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // 표시할 페이지 번호 계산 (최대 5개)
  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      // 5페이지 이하면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 5페이지 초과
      if (currentPage <= 3) {
        // 앞쪽: 1, 2, 3, 4, ..., last
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        // 뒤쪽: 1, ..., last-3, last-2, last-1, last
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // 중간: 1, ..., current-1, current, current+1, ..., last
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }

    return pages;
  };

  // 현재 표시 범위 계산
  const startItem = totalCount ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = totalCount ? Math.min(currentPage * pageSize, totalCount) : 0;

  return (
    <div className={`flex items-center justify-between gap-4 py-3 ${className}`}>
      {/* 왼쪽: 항목 표시 정보 */}
      <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
        {totalCount !== undefined && (
          <span>
            {totalCount > 0 ? (
              <>
                <span className="font-medium text-[var(--color-text-secondary)]">
                  {startItem.toLocaleString()}-{endItem.toLocaleString()}
                </span>
                {" / "}
                <span>{totalCount.toLocaleString()}개</span>
              </>
            ) : (
              "항목 없음"
            )}
          </span>
        )}

        {/* 페이지 크기 선택 */}
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="ml-2 px-2 py-1 text-sm bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-yellow)]"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}개씩
              </option>
            ))}
          </select>
        )}
      </div>

      {/* 오른쪽: 페이지 네비게이션 */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          {/* 처음으로 */}
          <IconButton
            icon={<ChevronsLeft size={16} />}
            label="처음 페이지"
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={!canGoPrev}
          />

          {/* 이전 */}
          <IconButton
            icon={<ChevronLeft size={16} />}
            label="이전 페이지"
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!canGoPrev}
          />

          {/* 페이지 번호들 */}
          <div className="flex items-center gap-0.5 mx-1">
            {getPageNumbers().map((page, idx) =>
              page === "..." ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="w-8 text-center text-[var(--color-text-muted)]"
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`
                    min-w-[32px] h-8 px-2 text-sm font-medium rounded-md transition-colors
                    ${
                      page === currentPage
                        ? "bg-[var(--color-pad-yellow)] text-[var(--color-accent-yellow-text)]"
                        : "text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]"
                    }
                  `}
                >
                  {page}
                </button>
              )
            )}
          </div>

          {/* 다음 */}
          <IconButton
            icon={<ChevronRight size={16} />}
            label="다음 페이지"
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!canGoNext}
          />

          {/* 마지막으로 */}
          <IconButton
            icon={<ChevronsRight size={16} />}
            label="마지막 페이지"
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={!canGoNext}
          />
        </div>
      )}
    </div>
  );
}
